import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/hooks/useDarkMode';
import { DarkModeToggle } from '@/components/ui/DarkModeToggle';
import { SkipNavigation } from '@/components/ui/SkipNavigation';
import { AnnouncementBanner } from '@/features/announcements/AnnouncementBanner';
import { useAuthStore } from '@/features/auth/hooks/useAuth';
import { apiClient, type ApiResponse } from '@/lib/api-client';

interface AppLayoutProps {
  children: ReactNode;
  sidebar?: ReactNode;
}

/**
 * Main application layout for the Back-Office app.
 * Responsive: sidebar collapses to mobile menu on small viewports (min 320px).
 * Supports dark mode toggle with system preference detection and localStorage persistence.
 * Includes skip navigation for keyboard accessibility (WCAG 2.4.1).
 * Proper heading hierarchy and landmark roles for screen readers.
 *
 * Validates: Requirements 24.1, 24.3, 24.5
 */
export function AppLayout({ children, sidebar }: AppLayoutProps) {
  const { mode, setMode } = useDarkMode();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Load the firm's branding colors (Settings → Branding) to tint the header.
  const { data: branding } = useQuery({
    queryKey: ['settings', 'branding', 'colors'],
    queryFn: async () => {
      const res =
        await apiClient.get<
          ApiResponse<{ primaryColor: string | null; secondaryColor: string | null }>
        >('/settings/branding');
      return res.data.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Subtle brand gradient tint for the header — same 135deg primary → secondary
  // direction used on the login screen, at low opacity so it reads as a wash
  // over the surface and keeps header content legible. Matches the portal.
  const headerGradientStyle: React.CSSProperties | undefined = (() => {
    const from = branding?.primaryColor;
    const to = branding?.secondaryColor || branding?.primaryColor;
    if (!from) return undefined;
    const rgba = (hex: string, alpha: number): string => {
      const c = hex.replace('#', '');
      if (c.length !== 6) return hex;
      const n = parseInt(c, 16);
      if (Number.isNaN(n)) return hex;
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
    };
    return {
      backgroundImage: `linear-gradient(135deg, ${rgba(from, 0.16)} 0%, ${rgba(to as string, 0.16)} 100%)`,
    };
  })();

  return (
    <>
      <SkipNavigation mainContentId="main-content" />
      <div className="flex min-h-screen min-w-[320px] bg-surface dark:bg-surface-dark">
        {/* Mobile header */}
        <div
          className="fixed top-0 left-0 right-0 z-40 flex h-14 items-center justify-between border-b border-border bg-white px-4 dark:border-border-dark dark:bg-surface-dark-secondary md:hidden"
          style={headerGradientStyle}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-400 dark:hover:bg-surface-dark-tertiary"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                )}
              </svg>
            </button>
            <span className="text-sm font-bold text-gray-900 dark:text-gray-100">Back-Office</span>
          </div>
          <DarkModeToggle mode={mode} onModeChange={setMode} />
        </div>

        {/* Mobile navigation overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 md:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        {sidebar && (
          <div
            className={cn(
              'fixed inset-y-0 left-0 z-40 transition-transform duration-200 md:relative md:translate-x-0',
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
            )}
          >
            {sidebar}
          </div>
        )}

        {/* Main content area */}
        <div className="flex flex-1 flex-col">
          {/* Desktop header with user profile and dark mode toggle */}
          <header
            className="hidden h-16 items-center justify-end gap-4 border-b border-border bg-white px-6 dark:border-border-dark dark:bg-surface-dark md:flex"
            style={headerGradientStyle}
            role="banner"
          >
            <UserProfileBadge />
            <DarkModeToggle mode={mode} onModeChange={setMode} />
          </header>

          {/* Main content */}
          <main
            id="main-content"
            className="flex-1 overflow-y-auto pt-14 md:pt-0"
            role="main"
            tabIndex={-1}
          >
            <AnnouncementBanner />
            <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</div>
          </main>
        </div>
      </div>
    </>
  );
}

// ─── User Profile Badge ───────────────────────────────────────────────────────

interface UserProfileData {
  name: string;
  email: string;
  jobRole: string | null;
  role: string | null;
}

function UserProfileBadge() {
  const authUser = useAuthStore((s) => s.user);

  const { data: profile } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<UserProfileData>>('/auth/me');
      return response.data.data;
    },
    staleTime: 60_000,
    enabled: !!authUser,
  });

  const name = profile?.name || authUser?.name || '';
  const roleName = profile?.role || authUser?.role || '';
  const jobRole = profile?.jobRole || authUser?.jobRole || '';

  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex items-center gap-3">
      <div className="text-right">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-tight">{name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
          {roleName}
          {jobRole && ` · ${jobRole.charAt(0).toUpperCase() + jobRole.slice(1)}`}
        </p>
      </div>
      <div
        className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700 dark:bg-brand-900 dark:text-brand-200"
        aria-hidden="true"
      >
        {initials || '?'}
      </div>
    </div>
  );
}
